/**
 * Customer detail "cover" — the rich profile-header section of S10.
 *
 * Composition:
 *   · Back arrow + avatar + name + status pills + tagline
 *   · Action row: [Edit ▾] dropdown · [Send reminder] · [New invoice] · [Archive]
 *     The Edit dropdown is the discoverable path for sections without
 *     a cover cell (Identity name, Tax, Terms). Cover cells themselves
 *     also carry inline pencil affordances for the fields they show.
 *   · 3-cell info grid: Contact / Address / GST & PAN
 *       - Contact + Address cells: pencil → inline edit; empty state
 *         renders InlineAddButton + a rationale line so missing data
 *         is unmissable per the UX brief.
 *       - GST cell: verified GST/PAN display; pencil opens Identity/GSTIN edit.
 *
 * @see prototype/sales/customer.html `.cover` — V1.1 baseline
 */
import {
  Check,
  ChevronDown,
  Clock,
  FileText,
  MapPin,
  MessageCircle,
  Pencil,
  Plus,
  ShieldCheck,
  Tag,
  User,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  cn,
} from "@flint/ui";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { customerSince, panFromGstin } from "../lib/format.ts";
import { BackButton } from "../../workspace/index.ts";
import { InheritedPill } from "./inherited-pill.tsx";
import { InlineAddButton } from "./inline-add-button.tsx";

type CustomerRow = z.infer<typeof contract.customerRow>;

export interface CustomerCoverEditActions {
  onEditIdentity: () => void;
  onEditContact: () => void;
  onEditAddress: () => void;
  onEditTax: () => void;
  onEditTerms: () => void;
  onEditCommunication: () => void;
  onEditTags: () => void;
}

export interface CustomerCoverProps {
  customer: CustomerRow;
  summary?: {
    invoiceCount: number;
    overdueInvoiceCount: number;
    oldestOverdueDays: number | null;
    behavior: string;
  };
  onArchive?: () => void;
  archiving?: boolean;
  /** Section-edit callbacks. Route owns the dialog state; cover triggers. */
  editActions: CustomerCoverEditActions;
  /** Fires a WhatsApp reminder against the customer's most-overdue open invoice. */
  onSendReminder?: () => void;
  reminderPending?: boolean;
  reminderJustSent?: boolean;
  /**
   * Non-null string disables the reminder button and surfaces the reason
   * as a tooltip. `null` (or omitted) = fully enabled.
   */
  reminderDisabledReason?: string | null;
}

export function CustomerCover({
  customer: c,
  summary,
  onArchive,
  archiving,
  editActions,
  onSendReminder,
  reminderPending,
  reminderJustSent,
  reminderDisabledReason,
}: CustomerCoverProps) {
  const isArchived = c.archivedAt !== null;
  const pan = panFromGstin(c.gstin);
  const city = c.city ?? "Unknown city";
  const paymentTerms = c.paymentTerms ?? "No terms set";
  const hasOverdue = (summary?.overdueInvoiceCount ?? 0) > 0;

  return (
    <section className="customer-detail-cover">
      <div className="customer-detail-cover-top">
        <BackButton fallbackTo="/sales/customers" label="Back to customers" />

        <div className="customer-detail-avatar" aria-hidden>
          {initials(c.name)}
        </div>

        <div className="customer-detail-head-body">
          <div className="customer-detail-name-row">
            <h1 className="customer-detail-name">{c.name}</h1>
            {isArchived ? (
              <span className="customer-detail-pill muted">Archived</span>
            ) : hasOverdue ? (
              <span className="customer-detail-pill late">
                Late · {summary?.oldestOverdueDays ?? 0}d overdue
              </span>
            ) : null}
            <span className="customer-detail-pill muted">
              Customer since {customerSince(c.createdAt)}
            </span>
          </div>
          <p className="customer-detail-tagline">
            {city}-based customer · <strong>{summary?.invoiceCount ?? 0}</strong>{" "}
            {(summary?.invoiceCount ?? 0) === 1 ? "invoice" : "invoices"} on record ·{" "}
            {summary?.behavior ?? "No invoice history yet"}
          </p>
          <div className="customer-detail-pills">
            {c.gstin ? (
              <span className="customer-detail-pill gst">
                <Check size={10} strokeWidth={2.4} aria-hidden />
                GSTIN verified
              </span>
            ) : null}
            <span
              className={hasOverdue ? "customer-detail-pill warn" : "customer-detail-pill muted"}
            >
              {summary?.behavior ?? "No invoice history"}
            </span>
            <span className="customer-detail-pill muted">{paymentTerms} terms</span>
          </div>
        </div>

        {!isArchived ? (
          <div className="customer-detail-actions">
            <EditDropdown actions={editActions} />
            <button
              className="customer-detail-btn secondary"
              type="button"
              onClick={onSendReminder}
              disabled={Boolean(reminderDisabledReason) || reminderPending}
              title={reminderDisabledReason ?? undefined}
              aria-disabled={Boolean(reminderDisabledReason) || undefined}
            >
              <MessageCircle size={13} strokeWidth={2} aria-hidden />
              {reminderPending
                ? "Sending…"
                : reminderJustSent
                  ? "Reminder sent"
                  : "Send reminder"}
            </button>
            <Link
              to="/sales/invoices/new"
              search={{ customerId: c.id }}
              className="customer-detail-btn primary"
            >
              <Plus size={14} strokeWidth={2.4} aria-hidden />
              New invoice
            </Link>
            {onArchive ? (
              <button
                className="customer-detail-btn ghost"
                type="button"
                onClick={onArchive}
                disabled={archiving}
              >
                {archiving ? "Archiving..." : "Archive"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="customer-detail-cover-grid">
        <CoverCell
          title="Contact"
          icon={<User size={12} strokeWidth={2} aria-hidden />}
          editLabel="Edit contact"
          onEdit={editActions.onEditContact}
        >
          {c.contactName || c.phone || c.phoneSecondary || c.email ? (
            <>
              {c.contactName ? (
                <p className="customer-detail-cover-line">
                  <strong>{c.contactName}</strong>
                </p>
              ) : null}
              {c.phone ? (
                <p className="customer-detail-cover-line">
                  <a href={`tel:+91${c.phone}`} className="num">
                    +91&nbsp; {formatPhone(c.phone)}
                  </a>
                  {!c.whatsappOnSecondary ? (
                    <span className="customer-detail-channel">
                      <MessageCircle size={9} strokeWidth={2.4} aria-hidden />
                      WhatsApp
                    </span>
                  ) : null}
                </p>
              ) : null}
              {c.phoneSecondary ? (
                <p className="customer-detail-cover-line">
                  <a href={`tel:+91${c.phoneSecondary}`} className="num">
                    +91&nbsp; {formatPhone(c.phoneSecondary)}
                  </a>
                  {c.whatsappOnSecondary ? (
                    <span className="customer-detail-channel">
                      <MessageCircle size={9} strokeWidth={2.4} aria-hidden />
                      WhatsApp
                    </span>
                  ) : null}
                </p>
              ) : null}
              {c.email ? (
                <p className="customer-detail-cover-line">
                  <a href={`mailto:${c.email}`}>{c.email}</a>
                </p>
              ) : null}
            </>
          ) : (
            <>
              <p className="customer-detail-cover-line muted">
                No contact yet — invoices fall back to email at the GST-registered address.
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                <InlineAddButton label="Add primary contact" onClick={editActions.onEditContact} />
                <InlineAddButton label="WhatsApp number" onClick={editActions.onEditContact} />
              </div>
            </>
          )}
        </CoverCell>

        <CoverCell
          title="Address"
          icon={<MapPin size={12} strokeWidth={2} aria-hidden />}
          headerExtra={
            c.gstin && c.addressLine1 ? <InheritedPill label="from GSTN" tone="verified" /> : null
          }
          editLabel="Edit address"
          onEdit={editActions.onEditAddress}
        >
          {c.addressLine1 || c.city || c.pincode || c.state || c.country ? (
            <>
              {c.addressLine1 ? (
                <p className="customer-detail-cover-line">{c.addressLine1}</p>
              ) : null}
              {c.addressLine2 ? (
                <p className="customer-detail-cover-line">{c.addressLine2}</p>
              ) : null}
              {c.city || c.pincode ? (
                <p className="customer-detail-cover-line">
                  <strong>
                    {c.city}
                    {c.city && c.pincode ? " " : ""}
                    {c.pincode ? <span className="num">{c.pincode}</span> : null}
                  </strong>
                </p>
              ) : null}
              {c.country ? (
                // Foreign/export customer — show the country (no Indian state).
                <p className="customer-detail-cover-line muted">{c.country}</p>
              ) : c.state ? (
                <p className="customer-detail-cover-line muted">{c.state}, India</p>
              ) : null}
            </>
          ) : (
            <>
              <p className="customer-detail-cover-line muted">
                No address on file — required on tax invoices over Rs 2.5L.
              </p>
              <div className="mt-1.5">
                <InlineAddButton label="Add address" onClick={editActions.onEditAddress} />
              </div>
            </>
          )}
        </CoverCell>

        <CoverCell
          title="GST &amp; PAN"
          icon={<ShieldCheck size={12} strokeWidth={2} aria-hidden />}
          headerExtra={c.gstin ? <InheritedPill label="from GSTN" tone="verified" /> : null}
          editLabel={c.gstin ? "Edit GSTIN" : "Add GSTIN"}
          onEdit={editActions.onEditIdentity}
        >
          {c.gstin ? (
            <>
              <p className="customer-detail-cover-line">
                <span className="num">{c.gstin}</span>
                <span className="customer-detail-gstin-tick">
                  <Check size={9} strokeWidth={2.4} aria-hidden />
                  Verified
                </span>
              </p>
              {pan ? (
                <p className="customer-detail-cover-line">
                  <span className="muted">PAN</span>
                  <span className="num">{pan}</span>
                </p>
              ) : null}
              {c.state ? (
                <p className="customer-detail-cover-line">
                  <strong>{c.state}</strong>
                </p>
              ) : null}
              <p className="customer-detail-cover-line muted">Tax mode resolved per invoice</p>
            </>
          ) : (
            <>
              <p className="customer-detail-cover-line muted">
                Unregistered counterparty — B2C / no GSTIN on file.
              </p>
              <div className="mt-1.5">
                <InlineAddButton label="Add GSTIN" onClick={editActions.onEditIdentity} />
              </div>
            </>
          )}
        </CoverCell>
      </div>

      {/* Slim cover-footer strips for placeholder sections.
          Communication is universal (every customer has channel /
          reminder / statement defaults), so it goes first. Tags +
          pinned note are per-customer extras, so they sit below.
          Each strip uses the same flush-bottom design: top border
          separator, no margin, full-width clickable. */}
      <CommunicationRow customer={c} onEdit={editActions.onEditCommunication} />
      <TagsAndNoteRow tags={c.tags} internalNote={c.internalNote} onEdit={editActions.onEditTags} />
    </section>
  );
}

function CommunicationRow({ customer: c, onEdit }: { customer: CustomerRow; onEdit: () => void }) {
  const hasPhone = Boolean(c.phone);
  const channelValue = describeChannel(c.defaultChannel, hasPhone);
  const cadenceValue = describeCadence({
    remind7dBefore: c.remind7dBefore,
    remind1dBefore: c.remind1dBefore,
    remindOnDue: c.remindOnDue,
    remind7dAfter: c.remind7dAfter,
  });
  const statementValue = c.monthlyStatement ? "1st monthly" : "Off";

  return (
    <button
      type="button"
      onClick={onEdit}
      className={cn(
        "group w-full",
        "flex items-center gap-3 px-4 py-2",
        "border-t border-line",
        "bg-gradient-to-r from-paper via-paper to-sky-50/40",
        "cursor-pointer transition-colors duration-100",
        "hover:from-sky-50/70 hover:via-sky-50/40 hover:to-sky-50/70",
        "focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-[-2px]",
      )}
      aria-label="Customize communication & invoicing"
    >
      <MessageCircle
        size={13}
        strokeWidth={2}
        className="text-muted group-hover:text-sky-700 flex-shrink-0"
        aria-hidden
      />
      <span className="text-xs font-semibold text-ink-2 group-hover:text-sky-700 flex-shrink-0">
        Communication
      </span>

      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
        <LabelValuePill
          tone={c.defaultChannel === "whatsapp" || c.defaultChannel === "email" ? "ok" : "muted"}
          label="Channel"
          value={channelValue}
          leading={
            <span
              aria-hidden
              className={cn(
                "inline-block w-1.5 h-1.5 rounded-full flex-shrink-0",
                c.defaultChannel === "whatsapp" || c.defaultChannel === "email"
                  ? "bg-ok"
                  : "bg-muted",
              )}
            />
          }
        />

        <LabelValuePill
          tone={cadenceValue === "Off" ? "muted" : "sky"}
          label="Reminders"
          value={cadenceValue}
          leading={<Clock size={10} strokeWidth={2.2} aria-hidden />}
        />

        <LabelValuePill
          tone={c.monthlyStatement ? "warn" : "muted"}
          label="Statement"
          value={statementValue}
          leading={<FileText size={10} strokeWidth={2.2} aria-hidden />}
        />

        <LabelValuePill
          tone={c.marketingOptIn ? "sky" : "muted"}
          label="Marketing"
          value={c.marketingOptIn ? "Opted in" : "Off"}
          leading={<MessageCircle size={10} strokeWidth={2.2} aria-hidden />}
        />
      </div>

      <span
        className={cn(
          "ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-sm flex-shrink-0",
          "text-xs font-semibold text-sky-700",
          "group-hover:bg-sky-100 transition-colors",
        )}
      >
        <Pencil size={11} strokeWidth={2} aria-hidden />
        Customize
      </span>
    </button>
  );
}

/**
 * Render a friendly label for the default channel. WhatsApp implies
 * a fallback to email per system-decisions §16 — surface that.
 */
function describeChannel(channel: string, hasPhone: boolean): string {
  switch (channel) {
    case "whatsapp":
      return hasPhone ? "WhatsApp + email" : "WhatsApp (no phone)";
    case "email":
      return "Email only";
    case "sms":
      return "SMS (OTP only)";
    case "print":
      return "Print only";
    default:
      return channel;
  }
}

/**
 * Compact reminder-cadence summary — `7d · 1d · on due` when several
 * are on; "Off" when none; a single label when just one is on.
 */
function describeCadence(c: {
  remind7dBefore: boolean;
  remind1dBefore: boolean;
  remindOnDue: boolean;
  remind7dAfter: boolean;
}): string {
  const parts: string[] = [];
  if (c.remind7dBefore) parts.push("7d");
  if (c.remind1dBefore) parts.push("1d");
  if (c.remindOnDue) parts.push("on due");
  if (c.remind7dAfter) parts.push("+7d");
  return parts.length === 0 ? "Off" : parts.join(" · ");
}

/**
 * Single combined row for Tags + pinned internal note. Empty state
 * shows ghost-pills as an affordance preview; filled state renders
 * the actual tags (sky-tinted pills) and a clamped one-line preview
 * of the note. Both states are clickable and open the same edit
 * dialog.
 */
function TagsAndNoteRow({
  tags,
  internalNote,
  onEdit,
}: {
  tags: readonly string[];
  internalNote: string | null;
  onEdit: () => void;
}) {
  const isEmpty = tags.length === 0 && !internalNote;

  if (isEmpty) {
    return (
      <button
        type="button"
        onClick={onEdit}
        className={cn(
          "group w-full",
          "flex items-center gap-3 px-4 py-2",
          "border-t border-line bg-paper",
          "cursor-pointer transition-colors duration-100",
          "hover:bg-sky-50/60",
          "focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-[-2px]",
        )}
        aria-label="Add tags and pinned note"
      >
        <Tag
          size={13}
          strokeWidth={2}
          className="text-muted group-hover:text-sky-700 flex-shrink-0"
          aria-hidden
        />
        <span className="text-xs font-semibold text-ink-2 group-hover:text-sky-700 flex-shrink-0">
          Tags &amp; note
        </span>

        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <span className="text-xs text-muted italic">No tags or note yet</span>
        </div>

        <span
          className={cn(
            "ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-sm flex-shrink-0",
            "text-xs font-semibold text-sky-700",
            "group-hover:bg-sky-100 transition-colors",
          )}
        >
          <Plus size={12} strokeWidth={2.4} aria-hidden />
          Add
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onEdit}
      className={cn(
        "group w-full text-left",
        "flex items-center gap-3 px-4 py-2",
        "border-t border-line bg-paper",
        "cursor-pointer transition-colors duration-100",
        "hover:bg-sky-50/40",
        "focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-[-2px]",
      )}
      aria-label="Edit tags and pinned note"
    >
      <Tag size={13} strokeWidth={2} className="text-muted flex-shrink-0" aria-hidden />
      <span className="text-xs font-semibold text-ink-2 group-hover:text-sky-700 flex-shrink-0">
        Tags &amp; note
      </span>
      <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
        {tags.length > 0 ? (
          tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-sm bg-sky-50 border border-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700"
            >
              {tag}
            </span>
          ))
        ) : null}
        {internalNote ? (
          <span className="text-xs text-ink-2 leading-snug italic truncate min-w-[12ch]">
            {internalNote}
          </span>
        ) : null}
      </div>
      <span
        className={cn(
          "ml-auto inline-grid place-items-center w-6 h-6 rounded-sm flex-shrink-0",
          "text-muted group-hover:text-sky-700 group-hover:bg-sky-100 transition-colors",
        )}
        aria-hidden
      >
        <Pencil size={11} strokeWidth={2} />
      </span>
    </button>
  );
}

/** Two-part inline chip — `LABEL · value` — used on the
 *  Communication strip so each fact tells the user *what* it is,
 *  not just the bare value. Tone tints the bg + label color so
 *  Channel / Reminders / Statement are visually distinct at a
 *  glance.
 *
 *  Tones reuse Flint's semantic tokens:
 *    · `ok`     — green (Channel-active, "delivery working")
 *    · `sky`    — blue  (Reminders, "scheduling")
 *    · `warn`   — amber (Statement, "doc / summary")
 *    · `muted`  — slate (fallback / inactive)
 */
function LabelValuePill({
  tone,
  label,
  value,
  leading,
}: {
  tone: "ok" | "sky" | "warn" | "muted";
  label: string;
  value: React.ReactNode;
  leading?: React.ReactNode;
}) {
  const toneClasses: Record<typeof tone, { wrap: string; label: string }> = {
    ok: {
      wrap: "bg-ok-50 border-ok-100",
      label: "text-ok",
    },
    sky: {
      wrap: "bg-sky-50 border-sky-100",
      label: "text-sky-700",
    },
    warn: {
      wrap: "bg-warn-50 border-warn-100",
      label: "text-warn",
    },
    muted: {
      wrap: "bg-bg border-line",
      label: "text-muted",
    },
  };
  const t = toneClasses[tone];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded border",
        "text-xs leading-none whitespace-nowrap",
        t.wrap,
      )}
    >
      {leading ? <span className={cn("flex-shrink-0", t.label)}>{leading}</span> : null}
      <span className={cn("font-bold uppercase tracking-wider text-xs", t.label)}>{label}</span>
      <span className="text-ink-2 font-semibold">{value}</span>
    </span>
  );
}

interface CoverCellProps {
  title: React.ReactNode;
  icon: React.ReactNode;
  headerExtra?: React.ReactNode;
  /** When provided, renders a pencil icon button in the cell's top-right. */
  onEdit?: () => void;
  editLabel?: string;
  children: React.ReactNode;
}

function CoverCell({
  title,
  icon,
  headerExtra,
  onEdit,
  editLabel = "Edit",
  children,
}: CoverCellProps) {
  return (
    <div className="customer-detail-cover-cell relative group">
      <div className="customer-detail-cover-cell-head">
        {icon}
        <span>{title}</span>
        {headerExtra ? <span className="ml-1">{headerExtra}</span> : null}
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={editLabel}
            className={cn(
              "ml-auto inline-grid place-items-center w-6 h-6 rounded-sm cursor-pointer",
              "text-muted hover:text-ink-2 hover:bg-bg transition-colors",
              "focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-1",
            )}
            title={editLabel}
          >
            <Pencil size={11} strokeWidth={2} aria-hidden />
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function EditDropdown({ actions }: { actions: CustomerCoverEditActions }) {
  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger asChild>
        <button className="customer-detail-btn secondary" type="button">
          <Pencil size={13} strokeWidth={2} aria-hidden />
          Edit
          <ChevronDown size={12} strokeWidth={2.2} aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuItem onSelect={actions.onEditIdentity}>
          Identity &amp; registration
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={actions.onEditContact}>Primary contact</DropdownMenuItem>
        <DropdownMenuItem onSelect={actions.onEditAddress}>Address</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={actions.onEditTax}>Tax &amp; TDS defaults</DropdownMenuItem>
        <DropdownMenuItem onSelect={actions.onEditTerms}>
          Terms, credit &amp; opening balance
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={actions.onEditCommunication}>
          Communication preferences
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={actions.onEditTags}>Tags &amp; internal notes</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] ?? "C"}${parts[1]?.[0] ?? parts[0]?.[1] ?? "U"}`.toUpperCase();
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)} ${digits.slice(5)}`;
}
